import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import MiniCalendar from "./MiniCalendar";
import { formatISODate, parseISODateLocal } from "../lib/dates";
import "./DatePicker.css";

function useDropdownPos(triggerRef, open) {
  const [pos, setPos] = useState(null);

  const update = useCallback(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    // MiniCalendar is roughly 280px tall; flip above if there's not enough room below.
    const POPUP_H = 300;
    const flipUp = rect.bottom + POPUP_H + 8 > window.innerHeight && rect.top > POPUP_H + 8;
    const top = flipUp ? Math.max(8, rect.top - POPUP_H - 4) : rect.bottom + 4;
    setPos({ top, left: rect.left, width: rect.width });
  }, [open, triggerRef]);

  useEffect(() => { update(); }, [update]);

  return pos;
}

export default function DatePicker({ value, onChange, clearable = true, shortYear = false, iconOnly = false }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const pos = useDropdownPos(triggerRef, open);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const today = new Date();
  // parseISODateLocal, not new Date("YYYY-MM-DD"): the latter parses as UTC
  // midnight and shows the previous day in negative-offset timezones.
  const parsed = value ? parseISODateLocal(value) : today;
  const displayYear = parsed.getFullYear();
  const displayMonth = parsed.getMonth();

  const displayText = value
    ? parsed.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: shortYear ? "2-digit" : "numeric" })
    : "";

  return (
    <div className="date-picker-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={`date-picker-trigger ${open ? "open" : ""} ${iconOnly ? "icon-only" : ""}`}
        onClick={() => setOpen(!open)}
        title={iconOnly ? displayText || "Datum" : undefined}
      >
        {!iconOnly && (
          <span className={value ? "" : "placeholder"}>{displayText || "Datum"}</span>
        )}
        {!iconOnly && clearable && value ? (
          <span
            className="date-picker-clear"
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
            title="Datum entfernen"
          >
            &times;
          </span>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        )}
      </button>
      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="date-picker-dropdown"
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MiniCalendar
            currentYear={displayYear}
            currentMonth={displayMonth}
            onSelect={(y, m, d) => { onChange(formatISODate(new Date(y, m, d))); setOpen(false); }}
            onClose={() => setOpen(false)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
